import { withQuery, type QueryValue } from '@/lib/routes/withQuery'
import { URGENT_CARE_BOOKING_QUERY } from '@/lib/urgentCare/locationsContract'

type BookingQuery = {
    [URGENT_CARE_BOOKING_QUERY.locationId]?: QueryValue
    [URGENT_CARE_BOOKING_QUERY.appointmentTime]?: QueryValue
    [URGENT_CARE_BOOKING_QUERY.walkIn]?: QueryValue
    /** Same-clinic reschedule when `"true"` or `"1"`. */
    [URGENT_CARE_BOOKING_QUERY.reschedule]?: QueryValue
}

type TimesQuery = {
    /** Preselected calendar day (`yyyy-MM-dd`) when arriving from a location preview. */
    date?: QueryValue
    /** Same-clinic reschedule when `"true"` or `"1"`. */
    [URGENT_CARE_BOOKING_QUERY.reschedule]?: QueryValue
}

/**
 * Patient-facing Urgent Care booking routes (GM-6956 → GM-6957 → GM-6958).
 * Paths are Next.js app routes, not the Rails API path.
 */
export const urgentCareRoutes = {
    order(orderId: string): string {
        return `/urgent-care/orders/${orderId}`
    },

    /**
     * Fulfillment-offer selection for an UrgentCare::Order (auto-selects when one Solv offer).
     * Example: /urgent-care/orders/order_123/options
     */
    options(orderId: string): string {
        return `${urgentCareRoutes.order(orderId)}/options`
    },

    /**
     * Bookable Solv location preview list for an UrgentCare::Order.
     * Example: /urgent-care/orders/order_123/locations
     */
    locations(orderId: string): string {
        return `${urgentCareRoutes.order(orderId)}/locations`
    },

    /** Full slot list for one Solv location (GM-6957). */
    locationTimes(orderId: string, locationId: string, query?: TimesQuery): string {
        return withQuery(
            `${urgentCareRoutes.locations(orderId)}/${encodeURIComponent(locationId)}/times`,
            query,
        )
    },

    /**
     * Booking confirm screen (GM-6958). Carries the chosen location + slot as
     * query params so refresh and deep links keep the selection.
     */
    booking(orderId: string, query?: BookingQuery): string {
        return withQuery(`${urgentCareRoutes.order(orderId)}/booking`, query)
    },

    /**
     * Post-booking acknowledgement. Reuses the booking query contract so the
     * booked page can re-resolve clinic display from the locations API.
     */
    booked(orderId: string, query?: BookingQuery): string {
        return withQuery(`${urgentCareRoutes.order(orderId)}/booked`, query)
    },
}

export type UrgentCareBookingSelection = {
    locationId: string
    /** ISO slot start for a scheduled booking; null for walk-in. */
    appointmentTime: string | null
    walkIn: boolean
    /** True when confirming a new time for an already-booked order. */
    reschedule: boolean
}

function isTruthyQueryFlag(value: string | null): boolean {
    return value === 'true' || value === '1'
}

/**
 * Reads location_id + appointment_time / walk_in from the GM-6957 → GM-6958 handoff.
 * Scheduled bookings require appointment_time; walk-in only needs location_id + walk_in.
 */
export function parseUrgentCareBookingSelection(
    searchParams: URLSearchParams | { get(name: string): string | null },
): UrgentCareBookingSelection | null {
    const locationId = searchParams.get(URGENT_CARE_BOOKING_QUERY.locationId)?.trim() ?? ''
    if (!locationId) return null
    const appointmentTimeRaw = searchParams.get(URGENT_CARE_BOOKING_QUERY.appointmentTime)?.trim() ?? ''
    const appointmentTime = appointmentTimeRaw || null
    const walkInRaw = searchParams.get(URGENT_CARE_BOOKING_QUERY.walkIn)
    const walkIn = walkInRaw === 'true' || walkInRaw === '1'
    const reschedule = isTruthyQueryFlag(searchParams.get(URGENT_CARE_BOOKING_QUERY.reschedule))

    if (!walkIn && !appointmentTime) return null

    return { locationId, appointmentTime, walkIn, reschedule}
}
